import { Alert, CircularProgress, Stack, Typography } from '@mui/material';
import {
  DisplayAlertErrors,
  IdentityFilter,
  IdentitySearchCriteria,
  IdentitySearchResults,
  SearchCriteria,
  TIdentitySearchResultsProps,
} from '@verifime/components';
import tokens from '@verifime/design-tokens';
import { useCustomerLookupData } from './hooks';

const SEARCH_RESULT_PAGE_SIZE = 20; // arbitary page size for now, intentionally small for now.

type TCustomerLookupCriteria = SearchCriteria<IdentityFilter>;

export interface CustomerLookupProps {
  isLegacyMode?: boolean;
  onCustomerClick?: TIdentitySearchResultsProps['onRowClick'];
  errorNotificationHandler?: (message: string) => void;
  'data-testid'?: string;
}

export default function CustomerLookup({
  isLegacyMode = true,
  onCustomerClick,
  errorNotificationHandler,
  'data-testid': dataTestId = 'customer-lookup',
}: CustomerLookupProps) {
  const placeholder = isLegacyMode ? undefined : 'Search by name or email';

  const { customers, isLoading, error, hasSearched, reset, handleSearch } = useCustomerLookupData({
    isLegacyMode,
    pageSize: SEARCH_RESULT_PAGE_SIZE,
    errorNotificationHandler,
  });

  const handleOnSearch = async (criteria: TCustomerLookupCriteria) => {
    await handleSearch(criteria.searchText, criteria.filters);
  };

  return (
    <Stack
      gap={tokens.spacingXs}
      mt={tokens.spacingBase}
      data-testid={dataTestId}
      data-component="CustomerLookup"
      aria-busy={isLoading}
    >
      <IdentitySearchCriteria
        onSearch={handleOnSearch}
        onCriteriaChanged={reset}
        placeholder={placeholder}
      />

      {isLoading && (
        <Stack alignItems="center" py={tokens.spacingXl}>
          <CircularProgress size={40} />
        </Stack>
      )}

      {error && (
        <DisplayAlertErrors errors={[{ message: 'Failed to load customers. Please try again.' }]} />
      )}

      {!isLoading && !error && customers.length > 0 && (
        <>
          {customers.length === SEARCH_RESULT_PAGE_SIZE && (
            <Alert severity="warning">
              Showing up to {SEARCH_RESULT_PAGE_SIZE} results. Refine your search or adjust filters
              to narrow results.
            </Alert>
          )}

          <IdentitySearchResults
            results={customers}
            onRowClick={onCustomerClick}
            data-testid={`${dataTestId}-results`}
          />
        </>
      )}

      {!isLoading && !error && customers.length === 0 && hasSearched && (
        <Typography variant="body1" color="textSecondary" align="center" py={tokens.spacingXl}>
          No customers found matching your search criteria.
        </Typography>
      )}

      {!isLoading && !error && customers.length === 0 && !hasSearched && (
        <Typography variant="body1" color="textSecondary" align="center" py={tokens.spacingXl}>
          Enter a search term to find customers.
        </Typography>
      )}
    </Stack>
  );
}
